import { Component, OnInit, Input, forwardRef, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl, Validator } from '@angular/forms';
import { PlinyService } from '../../../pliny/services/pliny.service';
import { IPatient } from '../../../pliny/interfaces/IPatient';
import { shareReplay } from 'rxjs/operators';

@Component({
  selector: 'app-patient-dropdown',
  templateUrl: './patient-dropdown.component.html',
  styleUrls: ['./patient-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PatientDropdownComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PatientDropdownComponent),
      multi: true
    }
  ]
})
export class PatientDropdownComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @Input() placeholder: string = 'Select Patient...';
  @Input() label: string = 'Patient';
  @Input() showLabel: boolean = true;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = true;
  @Input() filter: boolean = true;
  @Input() mrn: string | null = null;

  patientOptions: (IPatient & { displayLabel: string; dateOfBirth: Date | null })[] = [];
  selectedPatientId: string | null = null;
  isLoading: boolean = false;
  
  // Public getter to access patientOptions
  get patientOptionsList(): (IPatient & { displayLabel: string; dateOfBirth: Date | null })[] {
    return this.patientOptions;
  }

  private onChange = (value: string | null) => {};
  private onTouched = () => {};

  // Cache patients using shareReplay
  private patients$ = this.plinyService.getPatients('MaxResultCount=1000').pipe(
    shareReplay(1)
  );

  constructor(private plinyService: PlinyService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.loadPatients();
  }

  loadPatients() {
    this.isLoading = true;
    this.patients$.subscribe({
      next: (response) => {
        // Store full patient objects with displayLabel property for dropdown
        this.patientOptions = response.items.map(patient => {
          const fullName = `${patient.firstName || ''} ${patient.middleName || ''} ${patient.lastName || ''}`.trim();
          // Convert dateOfBirth string to Date object for date pipe
          let dateOfBirthDate: Date | null = null;
          if (patient.dateOfBirth) {
            const date = new Date(patient.dateOfBirth);
            dateOfBirthDate = Number.isNaN(date.getTime()) ? null : date;
          }
          return {
            ...patient,
            displayLabel: fullName || patient.mrn || 'Unknown Patient',
            dateOfBirth: dateOfBirthDate
          } as IPatient & { displayLabel: string; dateOfBirth: Date | null };
        });
        this.isLoading = false;
        // After loading patients, check if MRN should be used to pre-select
        this.selectPatientByMrn();
        // If value was already set (e.g. edit mode) before options loaded, force UI to update
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load patients:', error);
        this.isLoading = false;
      }
    });
  }

  // Public method to refresh the patient list
  refreshPatients(callback?: () => void) {
    // Recreate the observable to bypass cache
    this.patients$ = this.plinyService.getPatients('MaxResultCount=1000').pipe(
      shareReplay(1)
    );
    this.isLoading = true;
    this.patients$.subscribe({
      next: (response) => {
        // Store full patient objects with displayLabel property for dropdown
        this.patientOptions = response.items.map(patient => {
          const fullName = `${patient.firstName || ''} ${patient.middleName || ''} ${patient.lastName || ''}`.trim();
          // Convert dateOfBirth string to Date object for date pipe
          let dateOfBirthDate: Date | null = null;
          if (patient.dateOfBirth) {
            const date = new Date(patient.dateOfBirth);
            dateOfBirthDate = Number.isNaN(date.getTime()) ? null : date;
          }
          return {
            ...patient,
            displayLabel: fullName || patient.mrn || 'Unknown Patient',
            dateOfBirth: dateOfBirthDate
          } as IPatient & { displayLabel: string; dateOfBirth: Date | null };
        });
        this.isLoading = false;
        // Execute callback if provided
        if (callback) {
          callback();
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load patients:', error);
        this.isLoading = false;
        if (callback) {
          callback();
        }
      }
    });
  }

  selectPatientByMrn() {
    // Only pre-select by MRN if:
    // 1. MRN is provided
    // 2. No patient is currently selected (to avoid overriding form control value)
    // 3. Patient options are loaded
    if (this.mrn && !this.selectedPatientId && this.patientOptions.length > 0) {
      const matchingPatient = this.patientOptions.find(patient =>
        patient.mrn && patient.mrn.toLowerCase().trim() === this.mrn!.toLowerCase().trim()
      );

      if (matchingPatient && matchingPatient.id) {
        this.selectedPatientId = matchingPatient.id;
        this.onChange(this.selectedPatientId);
        this.onTouched();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // If MRN changes after initialization and patients are already loaded, try to select
    if (changes['mrn'] && !changes['mrn'].firstChange && this.patientOptions.length > 0) {
      // Only select if no patient is currently selected
      if (!this.selectedPatientId) {
        this.selectPatientByMrn();
      }
    }
  }

  onPatientChange(value: string | null) {
    this.selectedPatientId = value || null;
    this.onChange(this.selectedPatientId);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.selectedPatientId = value || null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator implementation
  validate(control: FormControl) {
    if (this.required && !control.value) {
      return { required: true };
    }
    return null;
  }
}

